import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styled from "styled-components";
import fireIcon from '../assets/fire.png';
import axios from "axios";
import { InfoCircleOutlined } from "@ant-design/icons";
import { withUIDirectives } from "components/utils/UI_Directive/showUIDirectives";
import openNotification from 'components/utils/CustomNotification';

const Streaks: React.FC = () => {
  const beAddress = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";

  const [streakInfo, setStreakInfo] = useState({
    self_current_streak: 0,
    self_current_max_streak: 0,
    org_current_max_streak: 0,
    org_current_streak: 0,
  });

  const [showStreakInfo, setShowStreakInfo] = useState(false);
  const [showStreakDetails, setShowStreakDetails] = useState(false);


  const cardRef = useRef<HTMLDivElement>(null);

  const fetchUserProfilestreak = useCallback(async () => {
    try {
      const response = await axios.get(`${beAddress}/api/user-current-streak`, {
        headers: {
          "user-uid": userUid,
        },
        withCredentials: true,
      });

      if (response.status === 200 && response.data.status === "Success") {
        const data = withUIDirectives(response.data);
        const userProfile = data.data;

        setStreakInfo({
          self_current_streak: userProfile.self_current_streak || 0,
          self_current_max_streak: userProfile.self_current_max_streak || 0,
          org_current_max_streak: userProfile.org_current_max_streak || 0,
          org_current_streak: userProfile.org_current_streak || 0,
        });

        localStorage.setItem("org_current_streak", userProfile.org_current_streak.toString());
      }
    } catch (error: any) {
      console.error("Error fetching user profile:", error);
    }
  }, [beAddress, sessionUid, userUid]);

  useEffect(() => {
    if (sessionUid && userUid) fetchUserProfilestreak();
  }, [fetchUserProfilestreak]);

  const ModalPortal = ({ children }: { children: React.ReactNode }) => {
    return ReactDOM.createPortal(children, document.body);
  };

  return (
    <StreaksContainer>
      <Card
        ref={cardRef}
        onClick={() => setShowStreakDetails(true)}
      >
        <Icon src={fireIcon} alt="Current Streak Icon" />
        <Content>
          <StreakCount>{streakInfo.org_current_streak}</StreakCount>
          <Label> Streak</Label>
        </Content>

        {/* --- REMOVED INFO ICON FROM HERE --- */}

        {/* --- DETAILS MODAL --- */}
        {showStreakDetails && (
          <ModalPortal>
            <ModalOverlay onClick={() => setShowStreakDetails(false)}>
              <StyledModalContent onClick={(e) => e.stopPropagation()}>
                <CloseButton onClick={() => setShowStreakDetails(false)}>&times;</CloseButton>

                {/* Header with Info Icon moved inside */}
                <Header>
                  <Title>Streak Details</Title>
                  <InfoCircleOutlined
                    onClick={() => setShowStreakInfo(true)}
                    style={{ fontSize: 20, color: "#007bff", cursor: "pointer", marginLeft: 10 }}
                    title="View Streak Info"
                  />
                </Header>

                <StreakGrid>
                  <StreakSection type="self">
                    <SectionTitle>My Streak</SectionTitle>
                    <StatRow>
                      <Label>Current</Label>
                      <Value highlight>{streakInfo.self_current_streak} 🔥</Value>
                    </StatRow>
                    <StatRow>
                      <Label>Max Streak</Label>
                      <Value>{streakInfo.self_current_max_streak}</Value>
                    </StatRow>
                  </StreakSection>

                  <VerticalDivider />

                  <StreakSection type="org">
                    <SectionTitle>Org Streak</SectionTitle>
                    <StatRow>
                      <Label>Current</Label>
                      <Value highlight>{streakInfo.org_current_streak} 🏢</Value>
                    </StatRow>
                    <StatRow>
                      <Label>Max Streak</Label>
                      <Value>{streakInfo.org_current_max_streak}</Value>
                    </StatRow>
                  </StreakSection>
                </StreakGrid>
              </StyledModalContent>
            </ModalOverlay>
          </ModalPortal>
        )}

        {/* --- INFO MODAL (Stacked) --- */}
        {showStreakInfo && (
          <ModalPortal>
            {/* Added z-index to make sure it appears ON TOP of the details modal */}
            <ModalOverlay onClick={() => setShowStreakInfo(false)} style={{ zIndex: 10000 }}>
              <ModalContent onClick={(e) => e.stopPropagation()}>
                <CloseButton onClick={() => setShowStreakInfo(false)}>&times;</CloseButton>
                <Header>
                  <Title>Streak Breakdown</Title>
                </Header>
                <img
                  src={require("../assets/streaks_i_info.png")}
                  alt="Streak Info"
                  style={{ width: "100%", borderRadius: 10, marginTop: 8 }}
                />
              </ModalContent>
            </ModalOverlay>
          </ModalPortal>
        )}
      </Card>
    </StreaksContainer>
  );
};

export default Streaks;

// --- STYLED COMPONENTS ---

const StreaksContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: row; 
  align-items: center;
  justify-content: flex-start;
  background: var(--card-background, #fff);
  padding: 12px 16px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  width: 100%;
  height: 100%;
  min-height: 80px;
  box-sizing: border-box;
  gap: 12px; 
  transition: transform 0.2s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
  }
`;

const Icon = styled.img`
  width: 36px;
  height: 36px;
  object-fit: contain;
  margin: 0;
  flex-shrink: 0;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const Label = styled.span`
  font-size: 11px;
  color: #888;
  font-weight: 500;
  margin-top: 2px;
  text-transform: uppercase;
`;

const StreakCount = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: var(--text-color, #333);
  margin: 0;
  line-height: 1.2;
`;

// --- MODAL STYLES ---
const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999; /* High z-index */
`;

const ModalContent = styled.div`
  background: #fff;
  padding: 24px;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.2);
  position: relative;
  text-align: center;
`;

const StyledModalContent = styled(ModalContent)`
  padding: 30px;
  max-width: 550px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 12px;
  background: transparent;
  border: none;
  font-size: 24px;
  cursor: pointer;
  z-index: 10;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
`;

// --- NEW SPLIT LAYOUT STYLES ---
const StreakGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  @media (min-width: 600px) {
    flex-direction: row;
    align-items: stretch;
    gap: 0;
  }
`;

const StreakSection = styled.div<{ type: string }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  border-radius: 12px;
  background-color: ${(props) => (props.type === "self" ? "#FFF8E1" : "#E0F7FA")};
`;

const VerticalDivider = styled.div`
  width: 100%;
  height: 1px;
  background: #EEE;
  margin: 10px 0;
  
  @media (min-width: 600px) {
    width: 1px;
    height: auto;
    margin: 0 16px;
  }
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #555;
  text-transform: uppercase;
  margin-bottom: 12px;
  letter-spacing: 0.5px;
`;

const StatRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 8px;
  
  &:last-child { margin-bottom: 0; }
`;

const Value = styled.span<{ highlight?: boolean }>`
  font-size: ${(props) => (props.highlight ? "24px" : "18px")};
  font-weight: 800;
  color: ${(props) => (props.highlight ? "#333" : "#666")};
`;